import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api`;

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldValidation {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  min?: number;
  max?: number;
  required?: boolean;
}

export interface FormField {
  id: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'multiselect' | 'date' | 'time' | 'number' | 'name' | 'companySize';
  label: string;
  placeholder?: string;
  required?: boolean;
  enabled?: boolean;
  order: number;
  options?: FormFieldOption[];
  validation?: FormFieldValidation;
  conditionalLogic?: Record<string, any>;
  settings?: Record<string, any>;

  // System field support
  isSystemField?: boolean;
  systemFieldKey?: 'name' | 'firstName' | 'lastName' | 'email' | 'company' | 'companySize' | 'phone' | 'notes';

  // Field-specific configuration
  nameOrder?: 'firstLast' | 'lastFirst';
  width?: 'full' | 'half' | 'third';
  helpText?: string;
}

export interface FormSettings {
  submitButtonText?: string;
  successMessage?: string;
  redirectUrl?: string;
  emailNotifications?: {
    enabled: boolean;
    recipients: string[];
    template?: string;
  };
  autoCreateContact?: boolean;
  autoCreateCompany?: boolean;
  assignOwner?: string;
  assigneeId?: number;
  tags?: string[];
  allowedDomains?: string[];
  enableCaptcha?: boolean;
  enableFileUploads?: boolean;
  maxFileSize?: string;
  allowedFileTypes?: string[];
}

export interface FormTemplate {
  id: string;
  organizationId: number;
  name: string;
  description?: string;
  category: 'contact' | 'company' | 'deal' | 'booking' | 'general' | 'feedback' | 'survey' | 'intake' | 'qualification' | 'onboarding';
  fields: FormField[];
  settings: FormSettings;
  styling?: Record<string, any>;
  isActive: boolean;
  isDefault: boolean;
  isGlobal: boolean;
  isPublic: boolean;
  publicSlug?: string;
  tags?: string[];
  usageCount: number;
  lastUsedAt?: Date;
  version: number;
  parentTemplateId?: string;
  externalId?: string;
  createdBy: number;
  updatedBy?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateFormTemplateInput {
  name: string;
  description?: string;
  category: FormTemplate['category'];
  fields: FormField[];
  settings?: FormSettings;
  styling?: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
  isPublic?: boolean;
  tags?: string[];
}

export interface UpdateFormTemplateInput {
  name?: string;
  description?: string;
  category?: FormTemplate['category'];
  fields?: FormField[];
  settings?: FormSettings;
  styling?: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
  isPublic?: boolean;
  tags?: string[];
}

export interface TemplateUsageResponse {
  templateId: string;
  usageCount: number;
  meetingTypes: Array<{
    id: number;
    title: string;
    slug: string;
    isActive: boolean;
  }>;
}

/**
 * FormTemplate Service
 * Handles all API communication for form templates
 */
class FormTemplateService {
  /**
   * Get all templates for the user's organization (includes global templates)
   */
  async getTemplates(filters?: {
    category?: string;
    isActive?: boolean;
    isDefault?: boolean;
    tags?: string[];
  }): Promise<FormTemplate[]> {
    try {
      const params = new URLSearchParams();

      if (filters?.category) {
        params.append('category', filters.category);
      }

      if (filters?.isActive !== undefined) {
        params.append('isActive', String(filters.isActive));
      }

      if (filters?.isDefault !== undefined) {
        params.append('isDefault', String(filters.isDefault));
      }

      if (filters?.tags && filters.tags.length > 0) {
        filters.tags.forEach(tag => params.append('tags', tag));
      }

      const response = await axios.get(`${API_URL}/form-templates?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data.data.templates || [];
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a single template by ID
   */
  async getTemplateById(id: string): Promise<FormTemplate> {
    try {
      const response = await axios.get(`${API_URL}/form-templates/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create a new template
   */
  async createTemplate(input: CreateFormTemplateInput): Promise<FormTemplate> {
    try {
      const response = await axios.post(`${API_URL}/form-templates`, input, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(id: string, updates: UpdateFormTemplateInput): Promise<FormTemplate> {
    try {
      const response = await axios.put(`${API_URL}/form-templates/${id}`, updates, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id: string): Promise<void> {
    try {
      await axios.delete(`${API_URL}/form-templates/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Duplicate a template (works for both global and organization templates)
   */
  async duplicateTemplate(id: string, newName?: string): Promise<FormTemplate> {
    try {
      const response = await axios.post(
        `${API_URL}/form-templates/${id}/duplicate`,
        { name: newName },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get built-in templates
   */
  async getBuiltInTemplates(): Promise<FormTemplate[]> {
    try {
      const response = await axios.get(`${API_URL}/form-templates/built-in`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Link a template to a meeting type
   */
  async linkToMeetingType(
    templateId: string,
    meetingTypeId: number,
    formTrigger: 'pre_booking' | 'post_booking' | 'confirmation' | 'reminder' | 'follow_up' | 'cancellation' | 'reschedule' | 'feedback' | 'custom' = 'pre_booking',
    isRequired: boolean = false,
    displayOrder: number = 1
  ): Promise<void> {
    try {
      await axios.post(
        `${API_URL}/form-templates/meeting-types/${meetingTypeId}/forms`,
        {
          formTemplateId: templateId,
          formTrigger,
          isRequired,
          displayOrder
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Unlink a template from a meeting type
   */
  async unlinkFromMeetingType(meetingTypeId: number, templateId: string): Promise<void> {
    try {
      await axios.delete(`${API_URL}/form-templates/meeting-types/${meetingTypeId}/forms`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        data: {
          formTemplateId: templateId
        }
      });
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get template usage statistics
   */
  async getTemplateUsage(templateId: string): Promise<TemplateUsageResponse> {
    try {
      const response = await axios.get(`${API_URL}/form-templates/${templateId}/usage`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Set a template as the organization's default
   */
  async setOrganizationDefault(templateId: string): Promise<FormTemplate> {
    try {
      const response = await axios.post(
        `${API_URL}/form-templates/${templateId}/set-default`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get the organization's default template
   */
  async getDefaultTemplate(): Promise<FormTemplate | null> {
    try {
      const response = await axios.get(`${API_URL}/form-templates/default`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get forms linked to a meeting type
   * PUBLIC ENDPOINT: No authentication required (used on public booking pages)
   * When includeDefault is true, the backend returns the org's default booking
   * template as a fallback if no forms are explicitly linked to the meeting type.
   */
  async getFormsForMeetingType(meetingTypeId: number, trigger?: string, includeDefault?: boolean): Promise<FormTemplate[]> {
    try {
      const params = new URLSearchParams();
      if (trigger) {
        params.append('trigger', trigger);
      }
      if (includeDefault) {
        params.append('includeDefault', 'true');
      }

      const response = await axios.get(
        `${API_URL}/booking/meeting-types/${meetingTypeId}/forms${params.toString() ? `?${params.toString()}` : ''}`
        // Public endpoint - no authentication required for booking page form display
      );

      // The response may contain MeetingTypeFormTemplate associations (with nested formTemplate)
      // or direct FormTemplate objects (when returning the org default fallback).
      const data = response.data.data || [];
      return data
        .map((item: any) => item.formTemplate || item)
        .filter((template: any) => template != null);
    } catch (error: any) {
      throw error;
    }
  }
}

const formTemplateService = new FormTemplateService();

export default formTemplateService;
